import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import { CancellationToken } from 'infra/cancellation'
import { action, computed, observable, makeObservable } from 'mobx'
import { Observable, Subject } from 'rxjs'
import { distinctUntilChanged } from 'rxjs/operators'
import { toStream } from 'utils/mobx-util'
import { shareLatest } from 'utils/rx-util'
import { WorkspaceSelected } from './DraftChange'
import { DraftStateSlice, PrepareContext } from './DraftStateSlice'
import { StepFactory, StepViewModel } from './OutlineViewModel'
import { JobCreationPorts } from './ports'
import { Id } from 'types/definitions'
import { OwnerScopedInput } from './PrefilledDraftInput'

const TITLE = 'Where are you submitting work?'

/**
 * View Model for selecting a Workspace for Job Creation.
 */
export default class WorkspaceSelectorViewModel extends DraftStateSlice {
  /**
   * The singleton step for this slice.
   */
  readonly step: StepViewModel

  /**
   * Used to emit changes so we know when to trigger a save.
   *
   * @private
   */
  private changesSubject = new Subject<WorkspaceSelected>()

  /**
   * Emits a value when the selected workspace was changed by the user.
   */
  changes$ = this.changesSubject.asObservable()

  /**
   * Selected workspace member.
   */
  @observable selectedWorkspaceMember: Member | null = null

  /**
   * Emits the selected Workspace Member (null included) on subscribe,
   * but only after `prepare` has been run.
   */
  selectedWorkspaceMember$ = toStream(
    () => this.selectedWorkspaceMember,
    true,
  ).pipe(
    distinctUntilChanged(),
    shareLatest(),
    // Don't start emitting until the slice is ready.
    this.whileReady(),
  )

  /**
   * Scoped to owner inputs.
   */
  @observable ownerScopedInput?: OwnerScopedInput

  /**
   * Constructor.
   */
  constructor(
    isActive$: Observable<boolean>,
    createStep: StepFactory,
    private adapter: JobCreationPorts,
    private _isDisabled: () => boolean,
  ) {
    super(isActive$)
    makeObservable(this)
    this.selectedWorkspaceMember$.subscribe()

    this.step = createStep({
      stepId: 'workspace',
      isValid: () => this.selectedWorkspace !== null,
      outlineItemDisplay: () => {
        if (!this.selectedWorkspace) {
          return {
            title: TITLE,
          }
        }
        return {
          title: TITLE,
          value: this.selectedWorkspace.name,
          visual: {
            type: 'Image',
            url: this.selectedWorkspace.branding.logos.icon,
          },
        }
      },
    })
  }

  /**
   * This is the only step container.
   * Relevant when we have more than 1 Workspace.
   */
  @computed
  get stepContainers() {
    // Only relevant if there is more than one Workspace to select from.
    return this.availableWorkspaces.length > 1 ? [this] : []
  }

  /**
   * Getter for the selected workspace.
   */
  @computed
  get selectedWorkspace() {
    return this.selectedWorkspaceMember?.workspace ?? null
  }

  /**
   * Whether this control is readonly.
   */
  @computed
  get disabled() {
    return this._isDisabled()
  }

  /**
   * The available workspaces for creating jobs.
   */
  @computed
  get availableWorkspaces() {
    const workspaces = this.adapter
      .availableWorkspaceMembers()
      .map((m) => m.workspace)

    if (!this.ownerScopedInput) {
      return workspaces
    }

    return workspaces.filter((w) =>
      this.ownerScopedInput?.workspaceIds.some((id) => id === Id.assert(w.id)),
    )
  }

  /**
   * Activates the VM and hydrates state from a Job.
   */
  async prepare(context: PrepareContext, ct = CancellationToken.default) {
    await ct.race(this.adapter.fetchAvailableWorkspaceMembers())
    ct.throwIfCancelled()

    if (context.type === 'EditDraft') {
      this.setWorkspace(context.job.workspace)
      return
    }

    // Must clear or set any owner scoped inputs before further preparation
    this.setOwnerScopeInput(context.prefilledDraft.ownerScopedInput)

    if (context.prefilledDraft.legendId) {
      const latestLegend = await this.adapter.fetchLatestLegendVersion(
        context.prefilledDraft.legendId,
      )
      this.selectWorkspace(latestLegend.workspace)
      return
    }

    const workspaceId = context.prefilledDraft.ownerScopedInput?.workspaceIds[0]
    const currentMember = this.adapter.getCurrentMember(workspaceId)
    this.selectWorkspace(currentMember.workspace)
  }

  /**
   * Sets whether component should be scoped to a job owner.
   *
   * @param scopedInput
   */
  @action
  setOwnerScopeInput(scopedInput?: OwnerScopedInput) {
    this.ownerScopedInput = scopedInput
  }

  /**
   * Sets the selected workspace.
   *
   * @param workspace
   */
  @action.bound
  setWorkspace(workspace: Workspace | null) {
    const member = this.adapter
      .availableWorkspaceMembers()
      .find((m) => m.workspace === workspace)
    this.selectedWorkspaceMember = member || null
  }

  /**
   * Selects the specified workspace and emits a notification.
   *
   * @param workspace
   */
  @action.bound
  selectWorkspace(workspace: Workspace | null | undefined) {
    workspace ??= null
    if (this.selectedWorkspace === workspace) {
      return
    }

    this.setWorkspace(workspace)
    this.changesSubject.next({ workspace, type: 'WorkspaceSelected' })
  }
}
